import React, { useState } from "react";

import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { twMerge } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import { EnumValuesTooltip } from "src/jsonSchema/viewer/EnumValuesTooltip";
import { JSONSchemaViewerDisplayOptions } from "src/jsonSchema/viewer/JSONSchemaRenderer";
import { PathBadge } from "src/jsonSchema/viewer/PathBadge";
import { PropertyBadge } from "src/jsonSchema/viewer/PropertyBadge";
import { SchemaValidationTooltip } from "src/jsonSchema/viewer/SchemaValidationTooltip";
import { TypeSwitchDropdown } from "src/jsonSchema/viewer/TypeSwitchDropdown";
import {
  ViewModelNode,
  ViewModelTree,
} from "src/jsonSchema/viewer/viewModelTreeBuilder";

type TreeRendererProps = {
  tree: ViewModelTree;
  displayOptions: JSONSchemaViewerDisplayOptions;
  openJsonSchemaWindow?: () => void;
};

export const TreeRenderer: React.FC<TreeRendererProps> = ({
  tree,
  displayOptions,
  openJsonSchemaWindow,
}) => {
  return (
    <div className="space-y-2">
      {tree.root.children.map((child) => (
        <NodeRenderer
          key={child.id}
          displayOptions={displayOptions}
          node={child}
          openJsonSchemaWindow={openJsonSchemaWindow}
        />
      ))}
    </div>
  );
};

type NodeRendererProps = {
  node: ViewModelNode;
  displayOptions: JSONSchemaViewerDisplayOptions;
  openJsonSchemaWindow?: () => void;
};

const NodeRenderer: React.FC<NodeRendererProps> = ({
  node,
  displayOptions,
  openJsonSchemaWindow,
}) => {
  const [isExpanded, setIsExpanded] = useState(node.initiallyExpanded);
  const [selectedUnionIndex, setSelectedUnionIndex] = useState(0);

  if (node.nodeType === "choice-only") {
    return (
      <ChoiceOnlyNodeRenderer displayOptions={displayOptions} node={node} />
    );
  }
  // For primitive properties without children or union choices
  if (node.children.length == 0 && node.unionChoices.length == 0) {
    return (
      <PrimitiveNodeRenderer
        displayOptions={displayOptions}
        node={node}
        openJsonSchemaWindow={openJsonSchemaWindow}
      />
    );
  }

  const onToggleExpanded = () => {
    if (displayOptions.canCollapse) {
      setIsExpanded(!isExpanded);
    }
  };

  // For complex properties (expandable or with union choices)
  return (
    <ComplexNodeRenderer
      displayOptions={displayOptions}
      isExpanded={isExpanded}
      node={node}
      openJsonSchemaWindow={openJsonSchemaWindow}
      selectedUnionIndex={selectedUnionIndex}
      onToggleExpanded={onToggleExpanded}
      onUnionIndexChange={setSelectedUnionIndex}
    />
  );
};

type PrimitiveNodeRendererProps = {
  node: ViewModelNode;
  displayOptions: JSONSchemaViewerDisplayOptions;
  openJsonSchemaWindow?: () => void;
};

const PrimitiveNodeRenderer: React.FC<PrimitiveNodeRendererProps> = ({
  node,
  displayOptions,
  openJsonSchemaWindow,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const hasCustomValidation = !!node.property.pattern;
  const getDataLoc = (name: string, dataPath: string) => {
    return `${name}-${dataPath.replace(/\./g, "-")}`;
  };
  const enumValues = "enum" in node.property ? (node.property.enum ?? []) : [];

  const renderTypeLabel = () => {
    return (
      <span className="ml-2 font-code-12 text-gray-400">{node.typeLabel}</span>
    );
  };
  return (
    <div
      className="flex h-[20px] min-w-0 items-center justify-between"
      data-loc={getDataLoc("json-schema-primitive", node.dataPath)}
    >
      <div className="flex min-w-0 flex-1 items-center truncate">
        <PropertyBadge
          dataPath={node.dataPath}
          displayOptions={displayOptions}
          icon={node.icon}
          isRequired={node.isRequired}
          name={node.name}
        />
        {displayOptions.showTypeLabels &&
          displayOptions.showPrimitiveTypeLabels &&
          node.typeLabel &&
          (enumValues.length > 0 ? (
            <EnumValuesTooltip enumValues={enumValues}>
              {renderTypeLabel()}
            </EnumValuesTooltip>
          ) : (
            renderTypeLabel()
          ))}
        {node.isNullable && (
          <span className="ml-2 font-code-12 text-gray-400">null</span>
        )}
        {hasCustomValidation && openJsonSchemaWindow && !renderedInDiffView && (
          <SchemaValidationTooltip
            actionText="View in JSON"
            title="Custom validation applied."
            onClick={openJsonSchemaWindow}
          />
        )}
      </div>
      {displayOptions.showDataPaths && displayOptions.showPrimitiveDataPath && (
        <div className="ml-2 flex-shrink-0">
          <PathBadge
            displayOptions={displayOptions}
            node={node}
            path={node.dataPath}
          />
        </div>
      )}
    </div>
  );
};

type ComplexNodeRendererProps = {
  node: ViewModelNode;
  displayOptions: JSONSchemaViewerDisplayOptions;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  selectedUnionIndex: number;
  onUnionIndexChange: (index: number) => void;
  openJsonSchemaWindow?: () => void;
};

const ComplexNodeRenderer: React.FC<ComplexNodeRendererProps> = ({
  node,
  displayOptions,
  isExpanded,
  onToggleExpanded,
  selectedUnionIndex,
  onUnionIndexChange,
  openJsonSchemaWindow,
}) => {
  // Get the currently selected union choice if this is a union
  const selectedChoice = node.unionChoices?.[selectedUnionIndex];

  // Determine chevron visibility based on expandability
  let expandable = false;
  if (!selectedChoice) {
    expandable = node.children.length > 0 || node.unionChoices.length > 0;
  } else if (selectedChoice && selectedChoice.node) {
    expandable =
      selectedChoice.node!.children.length > 0 ||
      selectedChoice.node!.unionChoices.length > 0;
  }
  const shouldShowChevron = expandable;
  const isChevronActive = expandable;

  // Determine what to render when expanded
  const getExpandedContent = () => {
    if (selectedChoice?.node) {
      if (selectedChoice?.node.children.length > 0) {
        return (
          <>
            {selectedChoice?.node.children.map((child) => (
              <NodeRenderer
                key={child.id}
                displayOptions={displayOptions}
                node={child}
              />
            ))}
          </>
        );
      } else {
        return (
          <NodeRenderer
            displayOptions={displayOptions}
            node={selectedChoice.node}
          />
        );
      }
    }
    if (node.children.length > 0) {
      return (
        <>
          {node.children.map((child) => (
            <NodeRenderer
              key={child.id}
              displayOptions={displayOptions}
              node={child}
              openJsonSchemaWindow={openJsonSchemaWindow}
            />
          ))}
        </>
      );
    }
    return null;
  };

  const shouldShowExpanded =
    isExpanded && (selectedChoice !== undefined || node.children.length > 0);
  const hasCustomLabelAndTypeRenderer =
    displayOptions.customLabelAndTypeRenderer != null;

  return (
    <div>
      {/* Complex property header */}
      <div
        className={isChevronActive ? "cursor-pointer hover:bg-gray-50" : ""}
        onClick={isChevronActive ? onToggleExpanded : undefined}
      >
        <div className="flex h-[20px] min-w-0 items-center justify-between font-inter-normal-12px">
          <div className="flex min-w-0 flex-1 items-center">
            {shouldShowChevron && (
              <Icon
                color={isChevronActive ? "text-gray-500" : "text-gray-300"}
                icon={isExpanded ? faChevronDown : faChevronRight}
                size="xs"
              />
            )}
            <span
              className={twMerge(
                "truncate font-inter-medium-12px text-gray-600",
                shouldShowChevron ? "ml-2" : "",
              )}
            >
              {(() => {
                const defaultRenderer = (
                  <PropertyBadge
                    bold={
                      (selectedChoice?.typeLabel ?? node.typeLabel) ===
                        "object" ||
                      (selectedChoice?.node?.isRef ?? node.isRef)
                    }
                    dataPath={node.dataPath}
                    displayOptions={displayOptions}
                    icon={selectedChoice?.typeIcon ?? node.icon}
                    isRequired={node.isRequired}
                    name={node.name}
                  />
                );

                if (hasCustomLabelAndTypeRenderer) {
                  return displayOptions.customLabelAndTypeRenderer?.(
                    node,
                    defaultRenderer,
                    selectedChoice,
                  );
                }
                return defaultRenderer;
              })()}
            </span>

            {/* Type switch dropdown for unions */}
            {node.unionChoices.length > 0 && (
              <div className="ml-2">
                <TypeSwitchDropdown
                  choices={node.unionChoices}
                  selectedIndex={selectedUnionIndex}
                  onSelectionChange={(index: number) => {
                    onUnionIndexChange(index);
                    // Auto-expand if switching to an expandable option
                    const newChoice = node.unionChoices![index];
                    if (
                      !isExpanded &&
                      newChoice.node &&
                      (newChoice.node.children.length > 0 ||
                        newChoice.node.unionChoices.length > 0)
                    ) {
                      onToggleExpanded();
                    }
                  }}
                />
              </div>
            )}

            {/* Type labels - only show if not using custom renderer */}
            {!hasCustomLabelAndTypeRenderer &&
              displayOptions.showTypeLabels &&
              node.unionChoices.length == 0 && (
                <>
                  <span className="ml-2 font-code-12 text-gray-400">
                    {node.typeLabel}
                  </span>
                  {node.isNullable && (
                    <span className="ml-2 font-code-12 text-gray-400">
                      null
                    </span>
                  )}
                </>
              )}
          </div>
          {displayOptions.showDataPaths && (
            <div className="ml-2 max-w-[60%] flex-shrink-1">
              <PathBadge
                displayOptions={displayOptions}
                node={node}
                path={node.dataPath}
              />
            </div>
          )}
        </div>
      </div>

      {/* Recursive nested content */}
      {shouldShowExpanded && (
        <div className="relative mt-2 ml-4">
          <div className="absolute -top-1 bottom-0 -left-1.5 w-px bg-gray-200"></div>
          <div className="ml-3 space-y-2">{getExpandedContent()}</div>
        </div>
      )}
    </div>
  );
};

type ChoiceOnlyNodeRendererProps = {
  node: ViewModelNode;
  displayOptions: JSONSchemaViewerDisplayOptions;
};

const ChoiceOnlyNodeRenderer: React.FC<ChoiceOnlyNodeRendererProps> = ({
  node,
  displayOptions,
}) => {
  const [selectedUnionIndex, setSelectedUnionIndex] = useState(0);

  if (!node.unionChoices || node.unionChoices.length === 0) {
    return null;
  }

  const selectedChoice = node.unionChoices[selectedUnionIndex];

  return (
    <div>
      {/* Choice-only header - just the dropdown */}
      <div className="flex h-[20px] min-w-0 flex-1 items-center gap-x-2">
        <Icon color="text-gray-500" icon={faChevronDown} size="xs" />
        <TypeSwitchDropdown
          choices={node.unionChoices}
          selectedIndex={selectedUnionIndex}
          onSelectionChange={setSelectedUnionIndex}
        />
      </div>

      {/* Render the selected choice only if it has a node */}
      {selectedChoice.node && (
        <div className="relative mt-2 ml-4">
          <div className="absolute top-0 bottom-0 left-0 w-px bg-gray-200"></div>
          <div className="ml-3 space-y-2">
            <NodeRenderer
              displayOptions={displayOptions}
              node={selectedChoice.node}
            />
          </div>
        </div>
      )}
    </div>
  );
};
